import { Moon, Sun, Monitor } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTheme } from '@/contexts/ThemeContext';
import { useSettingspageTranslations } from '@/hooks/useSettingspageTranslations';

export function ThemeToggle() {
  const { setTheme, theme } = useTheme();
  const i18n = useSettingspageTranslations();

  const themeOptions = [
    {
      value: 'light',
      label: i18n.preferences.themes.light,
      icon: Sun,
    },
    {
      value: 'dark',
      label: i18n.preferences.themes.dark,
      icon: Moon,
    },
    {
      value: 'system',
      label: i18n.preferences.themes.system,
      icon: Monitor,
    },
  ] as const;

  const currentTheme = themeOptions.find(option => option.value === theme);
  const CurrentIcon = currentTheme?.icon || Sun;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon" className="size-10">
          <CurrentIcon className="size-4" />
          <span className="sr-only">Toggle theme</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {themeOptions.map(option => {
          const Icon = option.icon;
          return (
            <DropdownMenuItem
              key={option.value}
              onClick={() => setTheme(option.value)}
              className={theme === option.value ? 'bg-accent' : ''}
            >
              <Icon className="mr-2 size-4" />
              <span>{option.label}</span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface ThemeSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  className?: string;
}

export function ThemeSelect({ value, onValueChange, className }: ThemeSelectProps) {
  const i18n = useSettingspageTranslations();

  const themeOptions = [
    { value: 'light', label: i18n.preferences.themes.light, icon: Sun },
    { value: 'dark', label: i18n.preferences.themes.dark, icon: Moon },
    { value: 'system', label: i18n.preferences.themes.system, icon: Monitor },
  ];

  return (
    <div className={`space-y-3 ${className}`}>
      {themeOptions.map(option => {
        const Icon = option.icon;
        return (
          <div
            key={option.value}
            className={`flex cursor-pointer items-center space-x-3 rounded-lg border p-3 transition-colors ${
              value === option.value
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
            }`}
            onClick={() => onValueChange(option.value)}
          >
            <div
              className={`rounded-full p-2 ${
                value === option.value
                  ? 'bg-primary-100 text-primary-600 dark:bg-primary-800 dark:text-primary-400'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
              }`}
            >
              <Icon className="size-4" />
            </div>
            <div className="flex-1">
              <p
                className={`font-medium ${
                  value === option.value
                    ? 'text-primary-900 dark:text-primary-100'
                    : 'text-gray-900 dark:text-gray-100'
                }`}
              >
                {option.label}
              </p>
              {option.value === 'system' && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {i18n.preferences.systemModeDescription}
                </p>
              )}
            </div>
            <div
              className={`size-4 rounded-full border-2 ${
                value === option.value
                  ? 'border-primary-500 bg-primary-500'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
            >
              {value === option.value && (
                <div className="size-full scale-50 rounded-full bg-white" />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
